import { computed, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, fromEvent, map, startWith } from 'rxjs';

export enum ScreenSize {
	MEDIUM = 'MEDIUM',
	SMALL = 'SMALL',
}

const SCREEN_MIN_WIDTH_MAP: Record<ScreenSize, number> = {
	[ScreenSize.MEDIUM]: 720,
	[ScreenSize.SMALL]: 0,
};

const SORTED_SIZES_SMALL_TO_LARGE = Object.keys(SCREEN_MIN_WIDTH_MAP).sort((a, b) => {
	const aValue = SCREEN_MIN_WIDTH_MAP[a as ScreenSize];
	const bValue = SCREEN_MIN_WIDTH_MAP[b as ScreenSize];

	if (aValue < bValue) {
		return -1;
	}

	if (aValue > bValue) {
		return 1;
	}

	return 0;
});

const MATCH_MEDIA_QUERIES = {
	hasMouse: '(hover: hover)',
};

@Injectable({ providedIn: 'root' })
export class ScreenService {
	public readonly $pixelSize = toSignal(
		fromEvent(window.visualViewport || window, 'resize').pipe(
			startWith(''),
			map(() => {
				return window.visualViewport?.width || window.innerWidth;
			}),
			distinctUntilChanged()
		),
		{ initialValue: window.visualViewport?.width || window.innerWidth }
	);

	public readonly $screen = computed(() => {
		const sizeInPixels = this.$pixelSize() || window.visualViewport?.width || window.innerWidth;

		let size = ScreenSize.SMALL;

		for (const key of SORTED_SIZES_SMALL_TO_LARGE) {
			if (sizeInPixels >= SCREEN_MIN_WIDTH_MAP[key as ScreenSize]) {
				size = key as ScreenSize;
			}
		}

		return size;
	});

	public readonly $isDesktop = computed(() => {
		return this.$screen() === ScreenSize.MEDIUM;
	});

	public readonly $hasMouse = (() => {
		const matchMedia = window.matchMedia(MATCH_MEDIA_QUERIES.hasMouse);

		const obs = fromEvent(matchMedia, 'change').pipe(
			startWith(matchMedia),
			map((event) => event as MediaQueryListEvent),
			map((event) => event.matches)
		);

		return toSignal(obs, { initialValue: matchMedia.matches });
	})();
}
